import { useCallback, useEffect, useMemo, useState } from 'react';
import { useOrg } from '../app/OrgContext.js';
import { currencySymbol } from '@goss/types';
import { apiFetch } from '../lib/api.js';
import { C } from '../lib/theme.js';
import { Card, PageHeader, InfoTip } from '../ui/ui.js';
import { METRIC_INFO as M } from '../lib/metricInfo.js';
import type { Reservation, ReservationsResponse, RoasReport } from '../lib/types.js';

/* Real Cloudbeds booking revenue + offer-code (promo) attribution + visuals. */

const REALIZED = new Set(['confirmed', 'checked_in', 'checked_out']);
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// Chart palette (theme desire/semantic colors) assigned by bucket order.
const PALETTE = [C.info, C.energy, C.calm, C.magic, C.freedom, C.good, C.watch];

function abbrev(n: number): string {
  const a = Math.abs(n);
  if (a >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (a >= 1_000) return (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'k';
  return String(Math.round(n));
}
function money(sym: string, n: number): string {
  return sym + Math.round(n).toLocaleString();
}
function daysBetween(a?: string | null, b?: string | null): number | null {
  if (!a || !b) return null;
  const d1 = new Date(a).getTime();
  const d2 = new Date(b).getTime();
  if (isNaN(d1) || isNaN(d2)) return null;
  return Math.round((d2 - d1) / 86_400_000);
}
function monthLabel(ym: string): string {
  const [y = '', m = '0'] = ym.split('-');
  return `${MONTHS[Number(m) - 1] ?? ''} '${y.slice(2)}`;
}

interface Bucket {
  key: string;
  count: number;
  revenue: number;
}
function bucketize(rows: Reservation[], keyOf: (r: Reservation) => string): Bucket[] {
  const m = new Map<string, Bucket>();
  for (const r of rows) {
    const key = keyOf(r);
    const b = m.get(key) ?? { key, count: 0, revenue: 0 };
    b.count++;
    b.revenue += Number(r.revenue) || 0;
    m.set(key, b);
  }
  return [...m.values()].sort((a, b) => b.revenue - a.revenue);
}

export function Bookings() {
  const { orgId, activeOrg } = useOrg();
  const [rows, setRows] = useState<Reservation[] | null>(null);
  const [campaigns, setCampaigns] = useState<{ id: string; name: string }[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!orgId) return;
    apiFetch<ReservationsResponse>(`/api/orgs/${orgId}/reservations?days=365`)
      .then((r) => setRows(r.rows))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Failed to load bookings'));
    // Campaign names for manual attribution of WhatsApp / direct bookings (RULES §7).
    apiFetch<RoasReport>(`/api/orgs/${orgId}/roas`)
      .then((d) =>
        setCampaigns(
          d.campaigns
            .filter((c) => c.campaign_id)
            .map((c) => ({ id: c.campaign_id, name: c.name ?? c.campaign_id })),
        ),
      )
      .catch(() => {});
  }, [orgId]);
  useEffect(() => {
    setRows(null);
    setError(null);
    load();
  }, [orgId, load]);

  async function tag(id: string, body: { campaign_id?: string | null; source?: string }) {
    try {
      await apiFetch(`/api/orgs/${orgId}/reservations/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not tag that booking');
    }
  }

  const s = useMemo(() => {
    if (!rows) return null;
    const realized = rows.filter((r) => REALIZED.has(String(r.status)));
    const revenue = realized.reduce((t, r) => t + (Number(r.revenue) || 0), 0);
    const coded = realized
      .filter((r) => r.promo_code)
      .reduce((t, r) => t + (Number(r.revenue) || 0), 0);
    const sym = currencySymbol(
      realized.find((r) => r.currency)?.currency ?? activeOrg?.default_currency,
    );

    const nights = realized
      .map((r) => daysBetween(r.checkin, r.checkout))
      .filter((n): n is number => n != null && n > 0);
    const leads = realized
      .map((r) => daysBetween(r.booked_at, r.checkin))
      .filter((n): n is number => n != null && n >= 0);
    const avgOf = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0);

    // Revenue by check-in month (chronological).
    const monthMap = new Map<string, number>();
    for (const r of realized) {
      if (!r.checkin) continue;
      const ym = r.checkin.slice(0, 7);
      monthMap.set(ym, (monthMap.get(ym) ?? 0) + (Number(r.revenue) || 0));
    }
    const byMonth = [...monthMap.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([ym, value]) => ({ label: monthLabel(ym), value }));

    const statusMix = bucketize(rows, (r) => String(r.status ?? 'unknown')).map((b) => ({
      key: b.key.replace(/_/g, ' '),
      count: b.count,
    }));

    const recent = [...realized]
      .sort((a, b) => String(b.booked_at ?? '').localeCompare(String(a.booked_at ?? '')))
      .slice(0, 12);

    // Bookings we can't auto-attribute (no promo code, not yet manually tagged) —
    // the coverage gap the user can close by hand (RULES §7).
    const untagged = [...realized]
      .filter((r) => !r.promo_code && !r.campaign_id)
      .sort((a, b) => String(b.booked_at ?? '').localeCompare(String(a.booked_at ?? '')))
      .slice(0, 25);

    return {
      sym,
      untagged,
      count: realized.length,
      revenue,
      avg: realized.length ? revenue / realized.length : 0,
      codedPct: revenue ? (coded / revenue) * 100 : 0,
      avgNights: avgOf(nights),
      avgLead: avgOf(leads),
      byCode: bucketize(realized, (r) => r.promo_code || '— no code —'),
      byChannel: bucketize(realized, (r) => r.source_name || 'Unknown'),
      byMonth,
      statusMix,
      recent,
      excluded: rows.length - realized.length,
    };
  }, [rows]);

  const loading = rows === null && !error;

  return (
    <div style={{ padding: '26px 30px 40px', animation: 'basilfade .3s ease' }}>
      <PageHeader eyebrow="CLOUDBEDS · realized bookings · last 365 days" title="Bookings" />

      {error && <Card style={{ padding: '16px 20px', color: C.act, fontSize: 13 }}>{error}</Card>}
      {loading && (
        <div style={{ color: C.muted, font: `500 12px 'IBM Plex Mono',monospace` }}>LOADING…</div>
      )}
      {rows && rows.length === 0 && !error && (
        <Card style={{ padding: 40, textAlign: 'center', color: C.muted, fontSize: 13 }}>
          No reservations yet. Sync Cloudbeds from Connections.
        </Card>
      )}

      {s && s.count > 0 && (
        <>
          {/* KPI row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 12 }}>
            <Kpi
              label="REALIZED REVENUE"
              info="realizedRevenue"
              value={money(s.sym, s.revenue)}
              accent
            />
            <Kpi label="BOOKINGS" info="bookingsCount" value={s.count.toLocaleString()} />
            <Kpi label="AVG BOOKING" info="avgBooking" value={money(s.sym, s.avg)} />
            <Kpi label="AVG NIGHTS" info="nights" value={s.avgNights.toFixed(1)} />
            <Kpi label="REVENUE W/ CODE" info="codedRevenue" value={`${s.codedPct.toFixed(0)}%`} />
          </div>

          {/* Revenue over time */}
          <Card style={{ padding: '16px 20px 14px', marginTop: 12 }}>
            <SectionTitle
              title="REVENUE OVER TIME"
              info="revenueOverTime"
              note="by check-in month"
            />
            <MonthlyBars data={s.byMonth} sym={s.sym} />
          </Card>

          {/* Code + channel */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
            <Card style={{ padding: '16px 20px 18px' }}>
              <SectionTitle
                title="REVENUE BY OFFER CODE"
                info="revenueByCode"
                note="= campaign / offer"
              />
              <HBars data={s.byCode} sym={s.sym} total={s.revenue} highlightCoded />
            </Card>
            <Card style={{ padding: '16px 20px 18px' }}>
              <SectionTitle
                title="REVENUE BY CHANNEL"
                info="revenueByChannel"
                note="how it reached you"
              />
              <DonutLegend data={s.byChannel} sym={s.sym} />
            </Card>
          </div>

          {/* Status + recent bookings */}
          <div
            style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 12, marginTop: 12 }}
          >
            <Card style={{ padding: '16px 20px 18px' }}>
              <SectionTitle title="BOOKING STATUS" info="bookingStatus" />
              <div style={{ marginTop: 8 }}>
                {s.statusMix.map((st, i) => (
                  <div
                    key={st.key}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '7px 0',
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: 2,
                          background: PALETTE[i % PALETTE.length],
                        }}
                      />
                      <span
                        style={{ fontSize: 12.5, color: C.secondary, textTransform: 'capitalize' }}
                      >
                        {st.key}
                      </span>
                    </span>
                    <span style={{ font: `600 13px 'Outfit',sans-serif`, color: C.primary }}>
                      {st.count}
                    </span>
                  </div>
                ))}
              </div>
            </Card>

            <Card style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: '15px 18px 4px' }}>
                <SectionTitle title="RECENT BOOKINGS" note="latest 12 confirmed" />
              </div>
              <RecentTable rows={s.recent} sym={s.sym} />
            </Card>
          </div>

          {/* Close the gap — tag WhatsApp / direct / code-less bookings by hand */}
          {s.untagged.length > 0 && (
            <Card style={{ padding: 0, overflow: 'hidden', marginTop: 14 }}>
              <div style={{ padding: '15px 18px 4px' }}>
                <SectionTitle
                  title="UNTAGGED BOOKINGS"
                  note={`${s.untagged.length} with no code — tag the source, or attribute to a campaign`}
                />
              </div>
              <UntaggedTable
                orgId={orgId!}
                rows={s.untagged}
                sym={s.sym}
                campaigns={campaigns}
                onTag={(id, body) => void tag(id, body)}
              />
            </Card>
          )}

          {/* Honest note */}
          <div
            style={{
              marginTop: 14,
              padding: '12px 16px',
              borderRadius: 10,
              background: C.surface2,
              border: `1px solid ${C.line}`,
              font: `500 11.5px 'IBM Plex Mono',monospace`,
              color: C.muted,
              lineHeight: 1.6,
            }}
          >
            Offer codes are real, honest attribution. Per-Meta-campaign ROAS arrives once campaigns
            register their codes; website bookings include ad-driven <em>and</em> organic traffic,
            so they aren&rsquo;t claimed as ad revenue. {s.excluded} cancelled/pending excluded.
          </div>
        </>
      )}
    </div>
  );
}

/* ── small pieces ─────────────────────────────────────────────────────────── */

function Kpi({
  label,
  info,
  value,
  accent,
}: {
  label: string;
  info: keyof typeof M;
  value: string;
  accent?: boolean;
}) {
  return (
    <Card style={{ padding: '16px 18px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          font: `500 9.5px 'IBM Plex Mono',monospace`,
          letterSpacing: '.08em',
          color: C.muted,
          marginBottom: 8,
        }}
      >
        {label}
        <InfoTip title={M[info].title} text={M[info].text} />
      </div>
      <div
        style={{
          font: `700 24px 'Outfit',sans-serif`,
          fontVariantNumeric: 'tabular-nums',
          color: accent ? C.good : C.primary,
        }}
      >
        {value}
      </div>
    </Card>
  );
}

function SectionTitle({
  title,
  info,
  note,
}: {
  title: string;
  info?: keyof typeof M;
  note?: string;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          font: `600 11px 'IBM Plex Mono',monospace`,
          letterSpacing: '.08em',
          color: C.secondary,
        }}
      >
        {title}
        {info && <InfoTip title={M[info].title} text={M[info].text} />}
      </span>
      {note && <span style={{ fontSize: 11, color: C.faint }}>{note}</span>}
    </div>
  );
}

function MonthlyBars({ data, sym }: { data: { label: string; value: number }[]; sym: string }) {
  const max = Math.max(1, ...data.map((d) => d.value));
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 6,
        height: 168,
        marginTop: 14,
      }}
    >
      {data.map((d) => (
        <div
          key={d.label}
          title={`${d.label}: ${money(sym, d.value)}`}
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 6,
            height: '100%',
          }}
        >
          <div style={{ font: `500 9px 'IBM Plex Mono',monospace`, color: C.faint }}>
            {d.value ? sym + abbrev(d.value) : ''}
          </div>
          <div
            style={{
              width: '68%',
              maxWidth: 40,
              height: `${(d.value / max) * 100}%`,
              minHeight: d.value ? 4 : 0,
              background: `linear-gradient(180deg, ${C.basil}, ${C.basilDeep})`,
              borderRadius: '4px 4px 0 0',
            }}
          />
          <div style={{ font: `500 9px 'IBM Plex Mono',monospace`, color: C.muted }}>{d.label}</div>
        </div>
      ))}
    </div>
  );
}

function HBars({
  data,
  sym,
  total,
  highlightCoded,
}: {
  data: Bucket[];
  sym: string;
  total: number;
  highlightCoded?: boolean;
}) {
  const max = Math.max(1, ...data.map((d) => d.revenue));
  return (
    <div style={{ marginTop: 12 }}>
      {data.map((d, i) => {
        const isCode = highlightCoded && !d.key.startsWith('—');
        const color = isCode ? C.good : PALETTE[i % PALETTE.length];
        return (
          <div key={d.key} style={{ padding: '8px 0' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                marginBottom: 5,
              }}
            >
              <span
                style={{
                  fontSize: 12.5,
                  color: isCode ? C.good : C.secondary,
                  fontWeight: isCode ? 600 : 500,
                }}
              >
                {d.key}
                <span style={{ color: C.faint, marginLeft: 7, fontSize: 11 }}>{d.count}×</span>
              </span>
              <span
                style={{
                  font: `600 12.5px 'Outfit',sans-serif`,
                  color: C.primary,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {money(sym, d.revenue)}
                <span style={{ color: C.faint, fontSize: 11, marginLeft: 6 }}>
                  {total ? ((d.revenue / total) * 100).toFixed(0) : 0}%
                </span>
              </span>
            </div>
            <div style={{ height: 7, background: C.lineSoft, borderRadius: 4, overflow: 'hidden' }}>
              <div
                style={{
                  width: `${(d.revenue / max) * 100}%`,
                  height: '100%',
                  background: color,
                  borderRadius: 4,
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DonutLegend({ data, sym }: { data: Bucket[]; sym: string }) {
  const total = data.reduce((t, d) => t + d.revenue, 0) || 1;
  const size = 132;
  const stroke = 16;
  const r = size / 2 - stroke / 2 - 2;
  const cx = size / 2;
  const circ = 2 * Math.PI * r;
  let offset = 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginTop: 12 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0 }}>
        <g transform={`rotate(-90 ${cx} ${cx})`}>
          {data.map((d, i) => {
            const len = (d.revenue / total) * circ;
            const seg = (
              <circle
                key={d.key}
                cx={cx}
                cy={cx}
                r={r}
                fill="none"
                stroke={PALETTE[i % PALETTE.length]}
                strokeWidth={stroke}
                strokeDasharray={`${len} ${circ - len}`}
                strokeDashoffset={-offset}
              />
            );
            offset += len;
            return seg;
          })}
        </g>
      </svg>
      <div style={{ flex: 1, minWidth: 0 }}>
        {data.map((d, i) => (
          <div
            key={d.key}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '5px 0',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              <span
                style={{
                  width: 9,
                  height: 9,
                  borderRadius: 2,
                  background: PALETTE[i % PALETTE.length],
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  fontSize: 12.5,
                  color: C.secondary,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {d.key}
              </span>
            </span>
            <span style={{ font: `600 12.5px 'Outfit',sans-serif`, color: C.primary }}>
              {money(sym, d.revenue)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

const SOURCES = ['whatsapp', 'phone', 'direct', 'meta', 'unknown'] as const;
function UntaggedTable({
  orgId,
  rows,
  sym,
  campaigns,
  onTag,
}: {
  orgId: string;
  rows: Reservation[];
  sym: string;
  campaigns: { id: string; name: string }[];
  onTag: (id: string, body: { campaign_id?: string | null; source?: string }) => void;
}) {
  // Guest names are fetched LIVE from Cloudbeds on demand and never stored.
  const [names, setNames] = useState<Record<string, string>>({});
  const [revealing, setRevealing] = useState(false);
  const [revealed, setRevealed] = useState(false);
  async function revealNames() {
    if (revealing) return;
    setRevealing(true);
    try {
      const ids = rows.map((r) => r.cloudbeds_res_id).filter(Boolean);
      const d = await apiFetch<{ names: Record<string, string> }>(
        `/api/orgs/${orgId}/cloudbeds/reservation-names`,
        { method: 'POST', body: JSON.stringify({ res_ids: ids }) },
      );
      setNames(d.names ?? {});
      setRevealed(true);
    } catch {
      /* leave unrevealed on failure */
    } finally {
      setRevealing(false);
    }
  }
  // Surface the Cloudbeds channel label + reservation ref so a bare date+price
  // row has something to cross-reference (RULES §7 — the unattributable tail).
  // Fixed widths (no 1fr) so REVENUE sits right after CHANNEL instead of being
  // shoved to the far right of a wide screen; slack goes to the right edge.
  const COLS = '118px 236px 104px 112px 210px';
  const sel: React.CSSProperties = {
    background: C.surface2,
    border: `1px solid ${C.line}`,
    borderRadius: 7,
    padding: '5px 8px',
    fontSize: 12,
    color: C.primary,
    fontFamily: 'inherit',
  };
  return (
    <div>
      <div style={{ padding: '4px 18px 10px', display: 'flex', justifyContent: 'flex-end' }}>
        {revealed ? (
          <span style={{ fontSize: 11, color: C.faint }}>
            Guest names fetched live from Cloudbeds — not stored.
          </span>
        ) : (
          <span
            onClick={() => void revealNames()}
            title="Fetch guest names from Cloudbeds for these rows. Shown for tagging, never saved."
            style={{
              fontSize: 11.5,
              fontWeight: 600,
              color: revealing ? C.muted : C.basil,
              cursor: revealing ? 'default' : 'pointer',
            }}
          >
            {revealing ? 'Fetching…' : '↓ Reveal guest names'}
          </span>
        )}
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: COLS,
          alignItems: 'center',
          padding: '8px 18px',
          background: C.surface2,
          borderTop: `1px solid ${C.line}`,
          borderBottom: `1px solid ${C.line}`,
          font: `500 9.5px 'IBM Plex Mono',monospace`,
          letterSpacing: '.08em',
          color: C.muted,
        }}
      >
        <span>CHECK-IN</span>
        <span>{revealed ? 'GUEST · CHANNEL' : 'CLOUDBEDS CHANNEL'}</span>
        <span style={{ textAlign: 'right' }}>REVENUE</span>
        <span style={{ paddingLeft: 14 }}>SOURCE</span>
        <span style={{ paddingLeft: 14 }}>ATTRIBUTE TO CAMPAIGN</span>
      </div>
      {rows.map((r) => (
        <div
          key={r.id}
          style={{
            display: 'grid',
            gridTemplateColumns: COLS,
            alignItems: 'center',
            padding: '9px 18px',
            borderBottom: `1px solid ${C.lineSoft}`,
            fontSize: 12.5,
            color: C.secondary,
          }}
        >
          <span style={{ minWidth: 0 }}>
            <div style={{ font: `500 12px 'IBM Plex Mono',monospace`, color: C.muted }}>
              {r.checkin ?? '—'}
            </div>
            {r.cloudbeds_res_id && (
              <div
                title="Cloudbeds reservation ID — look it up in Cloudbeds to see the guest & source"
                style={{
                  font: `500 9.5px 'IBM Plex Mono',monospace`,
                  color: C.faint,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  marginTop: 2,
                }}
              >
                #{r.cloudbeds_res_id}
              </div>
            )}
          </span>
          <span style={{ minWidth: 0, paddingRight: 12, overflow: 'hidden' }}>
            {names[r.cloudbeds_res_id] && (
              <div
                style={{
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: C.primary,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {names[r.cloudbeds_res_id]}
              </div>
            )}
            <div
              title={r.source_name ?? undefined}
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                color: C.muted,
                fontSize: names[r.cloudbeds_res_id] ? 11 : 12.5,
              }}
            >
              {r.source_name ?? '—'}
            </div>
          </span>
          <span
            style={{ textAlign: 'right', font: `600 12.5px 'Outfit',sans-serif`, color: C.primary }}
          >
            {r.revenue != null ? money(sym, Number(r.revenue)) : '—'}
          </span>
          <span style={{ paddingLeft: 14 }}>
            <select
              value={r.source}
              onChange={(e) => onTag(r.id, { source: e.target.value })}
              style={sel}
            >
              {SOURCES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </span>
          <span style={{ paddingLeft: 14 }}>
            <select
              value={r.campaign_id ?? ''}
              onChange={(e) => onTag(r.id, { campaign_id: e.target.value || null })}
              style={{ ...sel, maxWidth: 240 }}
            >
              <option value="">— not from a campaign —</option>
              {campaigns.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </span>
        </div>
      ))}
    </div>
  );
}

function RecentTable({ rows, sym }: { rows: Reservation[]; sym: string }) {
  // Flex space goes on CHANNEL (long labels), not CODE — otherwise CODE stretches
  // and leaves a dead gap while CHANNEL truncates.
  const COLS = '112px 56px 132px minmax(0,1fr) 104px';
  return (
    <div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: COLS,
          padding: '8px 18px',
          background: C.surface2,
          borderTop: `1px solid ${C.line}`,
          borderBottom: `1px solid ${C.line}`,
          font: `500 9.5px 'IBM Plex Mono',monospace`,
          letterSpacing: '.08em',
          color: C.muted,
        }}
      >
        <span>CHECK-IN</span>
        <span>NIGHTS</span>
        <span>CODE</span>
        <span>CHANNEL</span>
        <span style={{ textAlign: 'right' }}>REVENUE</span>
      </div>
      {rows.map((r) => {
        const nights = daysBetween(r.checkin, r.checkout);
        return (
          <div
            key={r.cloudbeds_res_id}
            style={{
              display: 'grid',
              gridTemplateColumns: COLS,
              alignItems: 'center',
              padding: '9px 18px',
              borderBottom: `1px solid ${C.lineSoft}`,
              fontSize: 12.5,
              color: C.secondary,
            }}
          >
            <span style={{ font: `500 12px 'IBM Plex Mono',monospace`, color: C.muted }}>
              {r.checkin ?? '—'}
            </span>
            <span>{nights ?? '—'}</span>
            <span
              style={{
                color: r.promo_code ? C.good : C.faint,
                fontWeight: r.promo_code ? 600 : 400,
              }}
            >
              {r.promo_code ?? '—'}
            </span>
            <span
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                color: C.muted,
              }}
            >
              {r.source_name ?? '—'}
            </span>
            <span
              style={{
                textAlign: 'right',
                font: `600 12.5px 'Outfit',sans-serif`,
                color: C.primary,
              }}
            >
              {r.revenue != null ? money(sym, Number(r.revenue)) : '—'}
            </span>
          </div>
        );
      })}
    </div>
  );
}
